import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Bell, UserPlus, UserCheck, Sparkles, CheckCheck, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const seconds = Math.floor((Date.now() - date) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
};

const iconFor = (type) => {
  switch (type) {
    case 'friend_request':
      return UserPlus;
    case 'friend_accepted':
      return UserCheck;
    case 'recommendation':
      return Sparkles;
    default:
      return Bell;
  }
};

const labelFor = (n) => {
  switch (n.type) {
    case 'friend_request':
      return { lead: n.fromUserName, body: 'sent you a friend request', tag: 'Friend' };
    case 'friend_accepted':
      return { lead: n.fromUserName, body: 'and you are now friends 🎉', tag: 'Friend' };
    case 'recommendation':
      return {
        lead: n.fromUserName,
        body: (n.note && n.note.trim())
          ? `"${n.note.trim()}"`
          : `recommends "${n.postTitle || 'a reflection'}"`,
        tag: 'Recommendation',
      };
    default:
      return { lead: 'New', body: 'notification', tag: '' };
  }
};

const tone = (type) => {
  switch (type) {
    case 'friend_request':
      return 'bg-blue-500/15 text-blue-300';
    case 'friend_accepted':
      return 'bg-emerald-500/15 text-emerald-300';
    case 'recommendation':
      return 'bg-[#D6AD60]/20 text-[#AA8347]';
    default:
      return 'bg-[#E8DDCB] text-stone-500';
  }
};

const NotificationsPanel = ({ open, onClose }) => {
  const { profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const items = (profile?.notifications || []).slice().reverse(); // newest first

  const token = () => localStorage.getItem('rbr_token');

  const markOne = async (id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${id}/seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      refreshProfile && refreshProfile();
    } catch (e) {
      console.warn('markOne notification failed', e);
    }
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/notifications/mark-seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      refreshProfile && refreshProfile();
    } finally {
      setBusy(false);
    }
  };

  const onItemClick = async (n) => {
    await markOne(n.id);
    onClose();
    if (n.type === 'recommendation' && (n.path || n.postId)) {
      navigate(n.path || `/post/${n.postId}`);
    } else if (n.type === 'friend_request' || n.type === 'friend_accepted') {
      navigate('/profile');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="notifications-panel-overlay"
    >
      <div
        className="bg-[#FFFCF3] border-x border-b border-[#D6AD60]/25 w-full max-w-[480px] rounded-b-3xl shadow-2xl animate-slide-down max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="notifications-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#D6AD60]" />
            <h3 className="text-base font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              Notifications
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {items.some((n) => !n.seen) && (
              <button
                onClick={markAll}
                disabled={busy}
                className="flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#D6AD60] transition-colors px-2 py-1 rounded-lg hover:bg-[#E8DDCB]"
                data-testid="notif-mark-all-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-[#E8DDCB] hover:bg-[#DBC9A3] flex items-center justify-center transition-colors"
              data-testid="notif-close-btn"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-[#D6AD60]" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {items.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#E8DDCB] flex items-center justify-center mx-auto mb-3">
                <Bell className="w-5 h-5 text-stone-500" />
              </div>
              <p className="text-sm text-stone-800">No notifications yet</p>
              <p className="text-[11px] text-stone-500 mt-1">
                Friend requests and recommendations will show up here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {items.map((n) => {
                const Icon = iconFor(n.type);
                const meta = labelFor(n);
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => onItemClick(n)}
                      className={`w-full flex items-start gap-3 px-5 py-3.5 text-left transition-colors hover:bg-[#E8DDCB] ${
                        n.seen ? 'opacity-60' : ''
                      }`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${tone(n.type)}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-stone-800 leading-snug">
                          <span className="font-semibold">{meta.lead}</span>{' '}
                          <span className="text-stone-500">{meta.body}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-stone-500">{formatTimeAgo(n.createdAt)}</span>
                          {!n.seen && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#D6AD60]" data-testid={`notif-dot-${n.id}`} />
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationsPanel;
